import { useUpdateTaskMutation } from 'api/collaborateApi';
import { TaskPriority, TaskStatus } from 'api/types/tasks';
import {
    ArrowUpDown,
    Check,
    ChevronDown,
    ChevronsDown,
    ChevronsUp,
    ChevronUp,
    CircleCheck,
    CircleDot,
    CircleDotDashed,
    CircleEllipsis
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskAttributes } from 'types/api/tasks';
import Button from 'ui/components/Button';
import DropdownMenu from 'ui/components/Dropdown';
import { cn } from 'utils/cn';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { getIsCompleteTasksFilterActive } from '../../../../../store/filtersSlice';
import {
    getPendingTask,
    getPendingTaskPriority,
    getPendingTaskStatus,
    setCreateModalOpen,
    setPendingTaskPriority,
    setPendingTaskStatus,
    setSelectedTaskId
} from '../../../../../store/tasksSlice';
import { getUser } from '../../../../../store/userSlice';

interface TaskStatusGroupProps {
    taskDetails?: Partial<TaskAttributes>;
    isPendingTask?: boolean;
}

// const lockTitle = "Content Management"
// const lockDescription = "Edit or delete comments with ease, keeping tasks clear, organized, and aligned with your workflow.."
// const lockModalImgSetting = 'https://wpfeedback-image.s3.us-east-2.amazonaws.com/images/integration-lock-images/lock-user-image.jpg'

export const TaskStatusGroup = ({
    taskDetails,
    isPendingTask,
}: TaskStatusGroupProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const pendingTask = useAppSelector(getPendingTask);
    const userInfo = useAppSelector(getUser)
    const isCompleteFilterActive = useAppSelector(getIsCompleteTasksFilterActive);
    const [updateTask, { isLoading }] = useUpdateTaskMutation();
    const pendingTaskStatus = useAppSelector(getPendingTaskStatus);
    const pendingTaskPriority = useAppSelector(getPendingTaskPriority);
    const status = isPendingTask ? pendingTaskStatus : taskDetails?.task_status?.find(s => s.selected)?.value as TaskStatus ?? 'open';
    const priority = isPendingTask ? pendingTaskPriority : taskDetails?.task_urgency?.find(p => p.selected)?.value as TaskPriority ?? 'low';

    // State for handling submenu visibility
    const [subMenu1, setSubMenu1] = useState(false);
    const [subMenu2, setSubMenu2] = useState(false);

    const statusIcons = {
        complete: <CircleCheck size={18} />,
        open: <CircleDot size={18} />,
        "in-progress": <CircleDotDashed size={18} />,
        "pending-review": <CircleEllipsis size={18} />
    };

    const priorityIcons = {
        low: <ChevronsDown size={18} />,
        medium: <ChevronDown size={18} />,
        high: <ChevronUp size={18} />,
        critical: <ChevronsUp size={18} />
    }

    // const locked = !userInfo?.raw?.plan?.team_management;

    // const handleLockInfo = () => {
    //     dispatch(setShowLockFeature({
    //         showLockFeature: true,
    //         lockTitle: lockTitle,
    //         lockParagraph: lockDescription,
    //         lockImg: lockModalImgSetting,
    //         rank: 2,
    //     }));
    // }

    // Handle status update
    const updateStatus = async (newStatus: TaskStatus) => {
        // if (locked) {
        //     handleLockInfo()
        //     return;
        // }

        if (isPendingTask && pendingTask) {
            dispatch(setPendingTaskStatus(newStatus));
        } else if (taskDetails && userInfo?.id) {
            try {
                await updateTask({
                    guest_user_id: '',
                    method: 'status',
                    task_id: taskDetails.task_id!,
                    user_id: userInfo.id,
                    value: newStatus,
                }).unwrap().then(() => {
                    // Close the modal if the task is marked as complete and the complete filter is not active
                    if (!isCompleteFilterActive && newStatus === 'complete') {
                        dispatch(setSelectedTaskId(null));
                        dispatch(setCreateModalOpen(false));
                    }
                });
            } catch (error) {
                console.error('Failed to update task status:', error);
            }
        }
    };

    // Handle priority update
    const updatePriority = async (newPriority: TaskPriority) => {
        // if (locked) {
        //     handleLockInfo()
        //     return;
        // }

        if (isPendingTask && pendingTask) {
            dispatch(setPendingTaskPriority(newPriority));
        } else if (taskDetails && userInfo?.id) {
            try {
                await updateTask({
                    guest_user_id: '',
                    method: 'priority',
                    task_id: taskDetails.task_id!,
                    user_id: userInfo.id,
                    value: newPriority,
                }).unwrap();
            } catch (error) {
                console.error('Failed to update task priority:', error);
            }
        }
    };

    // Sync pending task state
    useEffect(() => {
        if (isPendingTask && !pendingTask?.task_id.includes('PENDING')) {
            dispatch(setPendingTaskStatus(status));
            dispatch(setPendingTaskPriority(priority));
        }
    }, [isPendingTask, pendingTask?.task_id, status, priority, dispatch]);

    return (
        <DropdownMenu modal={false}>
            <DropdownMenu.Trigger asChild>
                <Button
                    size="icon"
                    variant="ghost"
                    tooltip={t('task_status.tooltip.status_priority', 'Set how important this is—and where it stands')}
                    toolTipPortal
                    loading={isLoading}
                    className="relative"
                >
                    <ArrowUpDown size={18} aria-hidden="true" />
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
                portal={true}
                container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
                align="start"
                className="w-56 flex flex-col gap-2 pointer-events-auto"
            >
                <DropdownMenu.Sub open={subMenu1} onOpenChange={setSubMenu1}>
                    <DropdownMenu.SubTrigger
                        onMouseOver={() => {
                            setSubMenu1(true);
                            setSubMenu2(false);
                        }}
                        className="flex items-center focus:bg-secondary focus:text-white hover:bg-secondary hover:text-white"
                    >
                        {statusIcons[status]}
                        <div className="capitalize ml-1">
                            {t(`task_status.status.${status}`, status.replace('-', ' '))}
                        </div>
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.SubContent
                        portal={false}
                        className="min-w-[208px]">
                        <DropdownMenu.Item
                            className={cn(
                                status === 'open' && 'bg-secondary text-white',
                                'focus:text-white focus:bg-secondary capitalize'
                            )}
                            onClick={() => updateStatus('open')}
                        >
                            <CircleDot size={18} className="mr-2" aria-hidden="true" />
                            {t('task_status.status.open', 'Open')}
                            {status === 'open' && <Check className="ml-auto" size={18} aria-hidden="true" />}
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            className={cn(
                                status === 'in-progress' && 'bg-ata-orange/50',
                                'focus:bg-ata-orange focus:text-textcolor capitalize'
                            )}
                            onClick={() => updateStatus('in-progress')}
                        >
                            <CircleDotDashed size={18} className="mr-2" aria-hidden="true" />
                            {t('task_status.status.in-progress', 'In Progress')}
                            {status === 'in-progress' && <Check className="ml-auto" size={18} aria-hidden="true" />}
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            onClick={() => updateStatus('pending-review')}
                            className={cn(
                                status === 'pending-review' && 'bg-ata-gold/50',
                                'focus:bg-ata-gold focus:text-textcolor capitalize'
                            )}
                        >
                            <CircleEllipsis size={18} className="mr-2" aria-hidden="true" />
                            {t('task_status.status.pending-review', 'Pending Review')}
                            {status === 'pending-review' && <Check className="ml-auto" size={18} aria-hidden="true" />}
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            className={cn(
                                status === 'complete' && 'bg-primary/50',
                                'focus:bg-primary focus:text-textcolor capitalize'
                            )}
                            onClick={() => updateStatus('complete')}
                        >
                            <CircleCheck size={18} className="mr-2" aria-hidden="true" />
                            {t('task_status.status.complete', 'Complete')}
                            {status === 'complete' && <Check className="ml-auto" size={18} aria-hidden="true" />}
                        </DropdownMenu.Item>
                    </DropdownMenu.SubContent>
                </DropdownMenu.Sub>
                <DropdownMenu.Sub open={subMenu2} onOpenChange={setSubMenu2}>
                    <DropdownMenu.SubTrigger
                        onMouseOver={() => {
                            setSubMenu2(true);
                            setSubMenu1(false);
                        }}
                        className={cn('flex items-center')}
                    >
                        {priorityIcons[priority]}
                        <span className='capitalize ml-1'>
                            {t(`task_status.priority.${priority}`, priority)}
                        </span>
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.SubContent
                        portal={false}
                        className="min-w-[208px]">
                        <DropdownMenu.Item
                            onClick={() => updatePriority('low')}
                            className={cn(
                                priority === 'low' && 'bg-primary/50',
                                'focus:bg-primary focus:text-textcolor capitalize'
                            )}
                        >
                            <ChevronsDown size={18} className="mr-2" aria-hidden="true" />
                            {t('task_status.priority.low', 'Low')}
                            {priority === 'low' && <Check className="ml-auto" size={18} aria-hidden="true" />}
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            onClick={() => updatePriority('medium')}
                            className={cn(
                                priority === 'medium' && 'bg-ata-gold/50',
                                'focus:bg-ata-gold focus:text-textcolor capitalize'
                            )}
                        >
                            <ChevronDown size={18} className="mr-2" aria-hidden="true" />
                            {t('task_status.priority.medium', 'Medium')}
                            {priority === 'medium' && <Check className="ml-auto" size={18} aria-hidden="true" />}
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            className={cn(
                                priority === 'high' && 'bg-ata-orange/50',
                                'focus:bg-ata-orange focus:text-textcolor capitalize'
                            )}
                            onClick={() => updatePriority('high')}
                        >
                            <ChevronUp size={18} className="mr-2" aria-hidden="true" />
                            {t('task_status.priority.high', 'High')}
                            {priority === 'high' && <Check className="ml-auto" size={18} aria-hidden="true" />}
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            className={cn(
                                priority === 'critical' && 'bg-destructive/50 text-white',
                                'focus:bg-destructive focus:text-white capitalize'
                            )}
                            onClick={() => updatePriority('critical')}
                        >
                            <ChevronsUp size={18} className="mr-2" aria-hidden="true" />
                            {t('task_status.priority.critical', 'Critical')}
                            {priority === 'critical' && <Check className="ml-auto" size={18} aria-hidden="true" />}
                        </DropdownMenu.Item>
                    </DropdownMenu.SubContent>
                </DropdownMenu.Sub>
            </DropdownMenu.Content>
        </DropdownMenu>
    );
};